import { useEffect, useCallback } from 'react';
import { interactiveSimulationsEventsService } from '@/events/interactiveSimulations';
import { Parameter } from '@/pages/InteractiveSimulation/types/replayInteractive';
import { WebSocketDataPoint, processWebSocketDataPoint } from '@/utils/InteractiveSimulation';
import { ChartUtilResult } from '@/components/General/PlayerChart';

export interface InteractiveSimulationEvent {
	type: string;
	data: WebSocketDataPoint[];
	currentTime?: string | number | Date;
}

export interface UseInteractiveSimulationEventsOptions {
	parameters: Parameter[];
	getChartDataMap: () => Map<number, ChartUtilResult>;
	onDataReceived: () => void;
	onReset: () => void;
	onIterationChange: () => void;
}


export function useInteractiveSimulationEvents(options: UseInteractiveSimulationEventsOptions) {
	const { parameters, getChartDataMap, onDataReceived, onReset, onIterationChange } = options;

	/**
	 * Maneja la recepción de datos del WebSocket
	 */
	const handleDataReceived = useCallback((dataPoints: WebSocketDataPoint[]) => {
		console.log({ dataPoints });
		const chartDataMap = getChartDataMap();
		let hasChanges = false;

		dataPoints.forEach((point) => {
			const processed = processWebSocketDataPoint(point, parameters, chartDataMap);
			if (processed) {
				hasChanges = true;
			}
		});

		if (hasChanges) {
			onDataReceived();
		}
	}, [parameters, getChartDataMap, onDataReceived]);

	/**
	 * Maneja el evento de reset del gráfico
	 */
	const handleReset = useCallback(() => {
		console.log('Resetting graph data');
		onReset();
	}, [onReset]);

	/**
	 * Maneja el cambio de iteración
	 */
	const handleIterationChange = useCallback(() => {
		console.log('Switching iteration, clearing graph data');
		onIterationChange();
	}, [onIterationChange]);

	useEffect(() => {
		const subscription = interactiveSimulationsEventsService
			.getSubject()
			.subscribe((event: InteractiveSimulationEvent) => {
				switch (event.type) {
					case 'dataSocketRecived':
						handleDataReceived(event.data);
						break;
					case 'resetGraph':
						handleReset();
						break;
					case 'setCurrentIteration':
						handleIterationChange();
						break;
					default:
						console.warn(`Unknown event type: ${event.type}`);
				}
			});

		return () => {
			subscription.unsubscribe();
		};
	}, [handleDataReceived, handleReset, handleIterationChange]);
}
